/* StripLak theme interactions:
   systems tabs, peel demo, scroll reveal, and mobile nav. */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function initSystems(root) {
    (root || document).querySelectorAll('[data-systems]').forEach(function (section) {
      var tabs = section.querySelectorAll('[data-system-tab]');
      var cards = section.querySelectorAll('[data-system-card]');
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.getAttribute('data-system-tab');
          tabs.forEach(function (t) {
            var active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          cards.forEach(function (c) {
            c.classList.toggle('is-active', c.getAttribute('data-system-card') === key);
          });
        });
      });
    });
  }

  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { el.classList.add('uk-rv'); io.observe(el); });
  }

  function initNav(root) {
    (root || document).querySelectorAll('[data-nav-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var menu = document.getElementById(btn.getAttribute('data-nav-toggle'));
        if (!menu) return;
        var open = menu.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  function initAccountPanel(root) {
    var panel = document.querySelector('[data-account-panel]');
    if (!panel || panel.getAttribute('data-account-ready') === 'true') return;
    panel.setAttribute('data-account-ready', 'true');

    var dialog = panel.querySelector('.account-panel__dialog');
    var openers = document.querySelectorAll('[data-account-open]');
    var closers = panel.querySelectorAll('[data-account-close]');
    var lastFocus = null;

    function openPanel(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('account-panel-open');
      window.history.replaceState(null, '', '#klantaccount');
      if (dialog) dialog.focus();
    }

    function closePanel(e) {
      if (e) e.preventDefault();
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('account-panel-open');
      if (window.location.hash === '#klantaccount') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    openers.forEach(function (opener) { opener.addEventListener('click', openPanel); });
    closers.forEach(function (closer) { closer.addEventListener('click', closePanel); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel(e);
    });
    if (window.location.hash === '#klantaccount') openPanel();
  }

  function initPeelDemo(root) {
    (root || document).querySelectorAll('[data-peel-demo]').forEach(function (demo) {
      if (demo.getAttribute('data-peel-ready') === 'true') return;
      demo.setAttribute('data-peel-ready', 'true');

      var stage = demo.querySelector('.cine-stage');
      var sceneTag = demo.querySelector('[data-scene-tag]');
      var chapters = demo.querySelector('[data-peel-chapters]');
      var switcher = demo.querySelector('[data-peel-switch]');
      var drips = Array.prototype.slice.call(demo.querySelectorAll('.drip'));
      if (!stage || !sceneTag || !chapters || !switcher) return;

      var setVar = function (key, value) { stage.style.setProperty(key, value); };
      var ease = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };
      var tri = function (t) { return 1 - Math.abs((t % 1) * 2 - 1); };
      var clamp = function (t) { return Math.max(0, Math.min(1, t)); };

      function resetProps() {
        setVar('--gun', 0);
        setVar('--coat', '0%');
        setVar('--dirt', 0);
        setVar('--dim', 0);
        setVar('--px', '-18%');
        setVar('--roll', 0);
        setVar('--patch', 0);
        setVar('--pfill', '0%');
        setVar('--zone', 0);
        drips.forEach(function (d) { d.style.height = '0'; });
      }

      var scene = {
        aanbrengen: function (p) {
          resetProps();
          setVar('--gun', 1);
          setVar('--gx', (10 + 80 * tri(p * 3.5)) + '%');
          setVar('--gy', (8 + 76 * p) + '%');
          setVar('--coat', (p * 104) + '%');
        },
        beschermen: function (p, keepPatch) {
          setVar('--gun', 0);
          setVar('--coat', '104%');
          setVar('--px', '-18%');
          setVar('--roll', 0);
          if (!keepPatch) setVar('--patch', 0);
          setVar('--dirt', clamp(p * 1.15));
          setVar('--dim', p * 0.16);
          drips.forEach(function (d, k) {
            d.style.height = (clamp(p * 1.6 - k * 0.25) * 15) + '%';
          });
        },
        overspuiten: function (p) {
          setVar('--coat', '104%');
          setVar('--dirt', 1);
          if (p < 0.18) {
            var q1 = p / 0.18;
            setVar('--zone', clamp(q1 * 1.3));
            setVar('--gun', clamp(q1));
            setVar('--gx', '10%');
            setVar('--gy', '60%');
            setVar('--patch', 0);
            setVar('--pfill', '0%');
            setVar('--dim', 0.16);
          } else if (p < 0.88) {
            var q2 = (p - 0.18) / 0.70;
            setVar('--zone', 1);
            setVar('--gun', 1);
            setVar('--patch', 1);
            setVar('--gx', (8 + 84 * tri(q2 * 3)) + '%');
            setVar('--gy', (59 + 34 * q2) + '%');
            setVar('--pfill', (q2 * 106) + '%');
            setVar('--dim', 0.16 * (1 - q2 * 0.8));
          } else {
            var q3 = (p - 0.88) / 0.12;
            setVar('--gun', 1 - q3);
            setVar('--zone', 1 - q3);
            setVar('--patch', 1);
            setVar('--pfill', '106%');
            setVar('--dim', 0.032);
          }
        },
        pellen: function (p) {
          var e = ease(p);
          setVar('--gun', 0);
          setVar('--coat', '104%');
          setVar('--dirt', 1);
          setVar('--px', (-18 + e * 140) + '%');
          setVar('--roll', e > 0.02 && e < 0.99 ? 1 : 0);
          setVar('--dim', 0.16 * (1 - e));
          return e;
        }
      };

      var modes = {
        single: [
          { n: 'Aanbrengen', d: 4200, f: function (p) { scene.aanbrengen(p); sceneTag.textContent = 'Aanbrengen'; } },
          { n: 'Beschermen', d: 3600, f: function (p) { scene.beschermen(p); sceneTag.textContent = 'Overspray vangt zich in de laag'; } },
          { n: 'Pellen', d: 3600, f: function (p) { var e = scene.pellen(p); sceneTag.textContent = e > 0.6 ? 'Schone wand, vol licht' : 'Alles in een keer pellen'; } },
          { n: '', d: 1400, f: function () { resetProps(); sceneTag.textContent = 'Klaar voor een nieuwe laag'; } }
        ],
        multi: [
          { n: 'Aanbrengen', d: 3800, f: function (p) { scene.aanbrengen(p); sceneTag.textContent = 'Aanbrengen'; } },
          { n: 'Beschermen', d: 3200, f: function (p) { scene.beschermen(p); sceneTag.textContent = 'Overspray vangt zich in de laag'; } },
          { n: 'Overspuiten', d: 3600, f: function (p) { scene.overspuiten(p); sceneTag.textContent = p < 0.18 ? 'Vervuiling zit vooral onderin' : (p < 0.88 ? 'Alleen de onderzijde overspuiten' : 'Onderkant weer wit'); } },
          { n: 'Pellen', d: 3400, f: function (p) { var e = scene.pellen(p); setVar('--patch', 1); setVar('--pfill', '106%'); setVar('--zone', 0); sceneTag.textContent = e > 0.5 ? 'Dikkere film pelt makkelijker' : 'Pas pellen als het nodig is'; } },
          { n: '', d: 1500, f: function () { resetProps(); sceneTag.textContent = 'Langere standtijd, minder stilstand'; } }
        ]
      };

      var mode = 'single';
      var bars = [];
      var chaps = [];
      var start = null;
      var offset = 0;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function buildChapters() {
        var phases = modes[mode].filter(function (p) { return p.n; });
        chapters.style.gridTemplateColumns = 'repeat(' + phases.length + ',1fr)';
        chapters.innerHTML = phases.map(function (p, k) {
          return '<button type="button" class="chap" data-i="' + k + '"><span class="mono">Stap ' + (k + 1) + '</span><b>' + p.n + '</b><span class="bar"><i></i></span></button>';
        }).join('');
        chaps = Array.prototype.slice.call(chapters.querySelectorAll('.chap'));
        bars = chaps.map(function (c) { return c.querySelector('.bar i'); });
        chaps.forEach(function (c) {
          c.addEventListener('click', function () {
            var index = Number(c.getAttribute('data-i'));
            var skip = 0;
            for (var k = 0; k < index; k++) skip += modes[mode][k].d;
            offset = skip;
            start = null;
          });
        });
      }

      function frame(ms) {
        var phases = modes[mode];
        var total = phases.reduce(function (sum, p) { return sum + p.d; }, 0);
        var t = ms % total;
        var index = 0;
        while (t > phases[index].d) {
          t -= phases[index].d;
          index += 1;
        }
        var progress = t / phases[index].d;
        bars.forEach(function (bar, k) { bar.style.width = (k < index ? 100 : (k === index ? progress * 100 : 0)) + '%'; });
        chaps.forEach(function (chap, k) { chap.classList.toggle('active', k === index); });
        phases[index].f(progress);
      }

      function loop(ts) {
        if (start === null) start = ts;
        frame(ts - start + offset);
        window.requestAnimationFrame(loop);
      }

      buildChapters();
      if (reduce) {
        resetProps();
        setVar('--coat', '104%');
        setVar('--dirt', 1);
        setVar('--px', '50%');
        setVar('--roll', 1);
        sceneTag.textContent = 'Pellen: vervuiling eraf, wand schoon';
        if (bars.length) {
          bars[bars.length - 1].style.width = '50%';
          chaps[chaps.length - 1].classList.add('active');
        }
      } else {
        window.requestAnimationFrame(loop);
      }

      switcher.addEventListener('click', function (e) {
        var btn = e.target.closest('button');
        if (!btn || btn.getAttribute('data-mode') === mode) return;
        mode = btn.getAttribute('data-mode');
        Array.prototype.slice.call(switcher.querySelectorAll('button')).forEach(function (x) {
          var active = x === btn;
          x.classList.toggle('on', active);
          x.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        resetProps();
        buildChapters();
        offset = 0;
        start = null;
      });
    });
  }

  function initAll(root) {
    initSystems(root);
    initReveal(root);
    initNav(root);
    initAccountPanel(root);
    initPeelDemo(root);
  }

  ready(function () { initAll(document); });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
